export interface ThreadEmail {
  senderName: string | null;
  senderEmail: string | null;
  fromUnderwriting: boolean;
  sentAt: Date | null;
  subject: string | null;
  body: string;
  headerless: boolean;
}

const HEADER_START = /^\s*From:\s*(.+)$/i;
const DATE_LINE = /^\s*(Date|Sent):\s*(.+)$/i;
const SUBJECT_LINE = /^\s*Subject:\s*(.*)$/i;
const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];

export function htmlToThreadText(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script, style, noscript, img").forEach((n) => n.remove());
  doc.querySelectorAll("br").forEach((n) => n.replaceWith("\n"));
  doc.querySelectorAll("p, div, tr, li, h1, h2, h3, h4, blockquote").forEach((n) => n.append("\n"));
  return (doc.body?.textContent ?? "").replace(/ /g, " ");
}

export function parseSender(raw: string): { name: string | null; email: string | null } {
  const email = raw.match(/[\w.+'-]+@[\w-]+(\.[\w-]+)+/)?.[0]?.toLowerCase() ?? null;
  const name = raw.replace(/<[^>]*>/g, "").replace(/[\w.+'-]+@[\w-]+(\.[\w-]+)+/g, "").replace(/["'<>[\]]/g, "").trim();
  return { name: name || null, email };
}

export function parseEmailDate(raw: string): Date | null {
  return parseNamedMonthDate(raw) ?? parseNumericDate(raw) ?? parseDayFirstDate(raw);
}

function toHour(hour: number, ampm: string | undefined): number {
  const a = ampm?.toUpperCase();
  if (a === "PM" && hour < 12) return hour + 12;
  if (a === "AM" && hour === 12) return 0;
  return hour;
}

function parseNumericDate(raw: string): Date | null {
  const m = raw.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4}),?\s+(\d{1,2}):(\d{2})(?::\d{2})?\s*([AP]M)?/i);
  if (!m) return null;
  const year = Number(m[3]) < 100 ? 2000 + Number(m[3]) : Number(m[3]);
  const d = new Date(year, Number(m[1]) - 1, Number(m[2]), toHour(Number(m[4]), m[6]), Number(m[5]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseDayFirstDate(raw: string): Date | null {
  const m = raw.match(/(\d{1,2})\s+([A-Za-z]+),?\s+(\d{4})(?:,?\s+(?:at\s+)?(\d{1,2}):(\d{2})(?::\d{2})?\s*([AP]M)?)?/i);
  if (!m) return null;
  const month = MONTHS.findIndex((x) => x.startsWith(m[2].toLowerCase().slice(0, 3)));
  if (month < 0) return null;
  const d = new Date(Number(m[3]), month, Number(m[1]), m[4] ? toHour(Number(m[4]), m[6]) : 12, m[5] ? Number(m[5]) : 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseNamedMonthDate(raw: string): Date | null {
  const m = raw.match(/([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})(?:\s+at)?\s+(\d{1,2}):(\d{2})(?::\d{2})?\s*([AP]M)?/i);
  if (!m) return null;
  const month = MONTHS.findIndex((x) => x.startsWith(m[1].toLowerCase().slice(0, 3)));
  if (month < 0) return null;
  let hour = Number(m[4]);
  const ampm = m[6]?.toUpperCase();
  if (ampm === "PM" && hour < 12) hour += 12;
  if (ampm === "AM" && hour === 12) hour = 0;
  const d = new Date(Number(m[3]), month, Number(m[2]), hour, Number(m[5]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function cleanBody(lines: string[]): string {
  return lines
    .map((l) => l.replace(/[ \t]+/g, " ").trimEnd())
    .join("\n")
    .replace(/​/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const isTeam = (email: string | null) => !!email && email.endsWith("@blogicsystems.com");

export function parseEmailThread(text: string): ThreadEmail[] {
  const lines = text.replace(/\r/g, "").split("\n");
  const emails: ThreadEmail[] = [];
  let current: ThreadEmail = { senderName: null, senderEmail: null, fromUnderwriting: false, sentAt: null, subject: null, body: "", headerless: true };
  let bodyLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const from = lines[i].match(HEADER_START);
    const window = lines.slice(i + 1, i + 6);
    const dateIdx = window.findIndex((l) => DATE_LINE.test(l));
    if (from && dateIdx >= 0) {
      current.body = cleanBody(bodyLines);
      emails.push(current);
      const sender = parseSender(from[1]);
      const date = window[dateIdx].match(DATE_LINE)![2];
      let j = i + 1 + dateIdx;
      let subject: string | null = null;
      for (let k = j + 1; k < Math.min(lines.length, j + 6); k++) {
        const s = lines[k].match(SUBJECT_LINE);
        if (s) { subject = s[1].trim() || null; j = k; break; }
        if (!/^\s*(To|Cc|Bcc):/i.test(lines[k]) && lines[k].trim() !== "") break;
        j = k;
      }
      current = {
        senderName: sender.name,
        senderEmail: sender.email,
        fromUnderwriting: !isTeam(sender.email),
        sentAt: parseEmailDate(date),
        subject,
        body: "",
        headerless: false,
      };
      bodyLines = [];
      i = j;
      continue;
    }
    bodyLines.push(lines[i]);
  }
  current.body = cleanBody(bodyLines);
  emails.push(current);

  const names = new Set(emails.map((e) => e.senderName?.trim().toLowerCase()).filter((n): n is string => !!n));
  for (const e of emails) e.body = withoutSignature(e.body, e.senderName ? new Set([e.senderName.trim().toLowerCase()]) : names);
  const kept = emails.filter((e) => e.body !== "");
  const oldestFirst = kept.reverse();
  for (let i = 1; i < oldestFirst.length; i++) {
    const prev = oldestFirst[i - 1].sentAt;
    const cur = oldestFirst[i].sentAt;
    if (prev && cur && cur.getTime() <= prev.getTime()) oldestFirst[i].sentAt = new Date(prev.getTime() + 60_000);
  }
  return oldestFirst;
}

function withoutSignature(body: string, names: Set<string>): string {
  const lines = body.split("\n");
  const cut = lines.findIndex((l, i) => i > 0 && names.has(l.replace(/[*_]/g, "").trim().toLowerCase()));
  return (cut > 0 ? lines.slice(0, cut) : lines).join("\n").trim();
}

export const bodyKey = (body: string) => body.replace(/\s+/g, " ").trim().toLowerCase().slice(0, 120);
